import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import equal from 'deep-equal';

import {
  getMvdError,
  hasPassportConfidenceErrors,
  isDocumentExists,
  isPassportConfidenceCorrect,
} from 'components/modules/MainAnketa/MainAnketa.helpers';
import { convertBirthPlaceVal, convertIssuerCode } from '../PersonalInfo.helpers';
import {
  KEY_COMPONENTS,
  PASSPORT_CONFIDENCE_ERRORS,
} from 'components/modules/MainAnketa/MainAnketa.constants';
import { DOCUMENTS_TYPES } from '../../FileUpload/FileUpload.constants';
import { NAMES } from './PassportForm.constants';

export const usePassportFormPresenter = ({
  persona,
  validate,
  errors,
  confidenceErrors,
  clearConfidenceErrors,
  isContentHidden,
  setFocusAt,
  onChange,
}) => {
  const { genderList } = useSelector((state) => state.referenceBooks);
  const { documents } = useSelector((state) => state.anketa);

  const [localErrors, setLocalErrors] = useState({});
  const [passportFormValues, setPassportFormValues] = useState(null);
  const [personalFormValues, setPersonalFormValues] = useState(null);
  const [showMore, setShowMore] = useState(false);

  const changeHandler = useCallback(
    (formValue) => {
      setPassportFormValues((previousFormValues) => {
        const mergedFormValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), { ...previousFormValues })
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedFormValues,
          id: persona.id,
          key: KEY_COMPONENTS.PASSPORT,
        });

        return mergedFormValues;
      });
    },
    [persona.id, onChange]
  );

  const changeHandlerPersonal = useCallback(
    (formValue) => {
      setPersonalFormValues((previousFormValues) => {
        const mergedFormValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), { ...previousFormValues })
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedFormValues,
          id: persona.id,
          key: KEY_COMPONENTS.PERSONAL_INFO_FORM,
        });

        return mergedFormValues;
      });
    },
    [persona.id, onChange]
  );

  const changeHandlerSeriaNumber = useCallback(
    (formValue) => {
      changeHandler({
        [NAMES.PASSPORT_SERIA_NUMBER]: formValue,
        [NAMES.PASSPORT_SERIES]: formValue.slice(0, 4) || '',
        [NAMES.PASSPORT_NUMBER]: formValue.slice(4, formValue.length) || '',
      });
    },
    [changeHandler]
  );

  const changeHandlerIssuerCode = useCallback(
    (formValue) => {
      if (formValue?.data) {
        changeHandler({
          [NAMES.PASSPORT_ISSUER_CODE]: formValue.data?.code ?? '',
          [NAMES.PASSPORT_ISSUER]: formValue.data?.name ?? passportFormValues.issuer,
        });

        setFocusAt(`${KEY_COMPONENTS.PASSPORT}_${NAMES.BIRTH_PLACE}_input`);
      } else {
        changeHandler({ [NAMES.PASSPORT_ISSUER_CODE]: convertIssuerCode(formValue.value) });
      }
    },
    [passportFormValues, setFocusAt, changeHandler]
  );

  const changeHandlerIssuer = useCallback(
    (formValue) => {
      if (formValue?.data) {
        changeHandler({
          [NAMES.PASSPORT_ISSUER_CODE]: formValue.data?.code ?? passportFormValues.issuerCode,
          [NAMES.PASSPORT_ISSUER]: formValue.data?.name ?? formValue.value,
        });
      } else {
        changeHandler({
          [NAMES.PASSPORT_ISSUER]: formValue.value?.toUpperCase().trim() ?? '',
        });
      }
    },
    [passportFormValues, changeHandler]
  );

  const changeHandlerBirthdayPlace = useCallback(
    (formValue) => {
      changeHandler(convertBirthPlaceVal(formValue));
    },
    [changeHandler]
  );

  const changeHandlerBirthDate = useCallback(
    (formValue) => {
      const normalizedValue =
        formValue[NAMES.BIRTHDAY_DATE]?.length === 2 ? '' : formValue[NAMES.BIRTHDAY_DATE];

      changeHandlerPersonal({ [NAMES.BIRTHDAY_DATE]: normalizedValue });
    },
    [changeHandlerPersonal]
  );

  const changeHandlerGender = useCallback(
    (formValue) => {
      changeHandlerPersonal({
        [NAMES.PERSON_GENDER]: genderList.find(
          (genderItem) => genderItem.id === formValue[NAMES.PERSON_GENDER]
        ),
      });
    },
    [genderList, changeHandlerPersonal]
  );

  const validation = useCallback(
    (path) => {
      setLocalErrors((prev) => ({ ...prev, [path]: errors[path] || null }));
      clearConfidenceErrors(path);
    },
    [errors, clearConfidenceErrors]
  );

  const getError = useCallback(
    (path) => {
      const validationError = errors[path];
      const generalError = localErrors[path];
      const hasMvdError = getMvdError(errors);

      if (!validate && !hasMvdError) {
        return generalError;
      }

      return validationError ?? generalError;
    },
    [validate, errors, localErrors]
  );

  const getConfidenceError = useCallback(
    (path) => {
      return Object.hasOwn(confidenceErrors, path);
    },
    [confidenceErrors]
  );

  const onHeaderClick = useCallback(() => {
    setShowMore((prev) => !prev);
  }, []);

  const isPassportError = useMemo(() => {
    if (!errors || !validate) {
      return false;
    }

    const PASSPORT_FIELDS = [
      PASSPORT_CONFIDENCE_ERRORS.ISSUE_DATE,
      PASSPORT_CONFIDENCE_ERRORS.SERIA_NUMBER,
    ];

    const normalizedErrors = Object.keys(errors).map((error) => error.replace(/\[\d+\]/g, ''));

    const hasPassportFieldError = normalizedErrors.some((error) =>
      PASSPORT_FIELDS.some((field) => error.includes(field))
    );

    const hasMvdError = getMvdError(errors);

    return hasPassportFieldError || hasMvdError;
  }, [errors, validate]);

  const shouldShowCheckIcon = useMemo(() => {
    return (
      isDocumentExists(documents) &&
      (isPassportConfidenceCorrect(documents) || !hasPassportConfidenceErrors(confidenceErrors)) &&
      !Object.keys(errors).some((error) => error.includes(DOCUMENTS_TYPES.PASSPORT))
    );
  }, [documents, errors, confidenceErrors]);

  const shouldShowAlertIcon = useMemo(() => {
    return (
      !isDocumentExists(documents) ||
      Object.keys(errors).some((error) => error.includes(DOCUMENTS_TYPES.PASSPORT))
    );
  }, [documents, errors]);

  useEffect(() => {
    if (!persona || equal(passportFormValues, persona[KEY_COMPONENTS.PASSPORT])) {
      return;
    }

    setPassportFormValues(persona[KEY_COMPONENTS.PASSPORT]);
  }, [persona]);

  useEffect(() => {
    if (!persona || equal(personalFormValues, persona[KEY_COMPONENTS.PERSONAL_INFO_FORM])) {
      return;
    }

    setPersonalFormValues(persona[KEY_COMPONENTS.PERSONAL_INFO_FORM]);
  }, [persona]);

  useEffect(() => {
    if (!documents || !isDocumentExists(documents) || !passportFormValues || !errors) {
      return;
    }

    if (isPassportError) {
      setShowMore(true);
    }
  }, [documents, passportFormValues, isPassportError, errors]);

  useEffect(() => {
    const passportCorrect = isPassportConfidenceCorrect(documents);

    if (passportCorrect !== null) {
      setShowMore(!passportCorrect);
    }
  }, [documents]);

  useEffect(() => {
    if (validate) {
      return;
    }

    setLocalErrors(
      Object.keys(localErrors).reduce((obj, key) => ({ ...obj, [key]: errors[key] || null }), {})
    );
  }, [validate, errors]);

  useEffect(() => {
    if (showMore && isContentHidden) {
      setShowMore(false);
    }
  }, [showMore, isContentHidden]);

  return {
    showMore,
    errors,
    documents,
    genderList,
    passportFormValues,
    personalFormValues,
    shouldShowAlertIcon,
    shouldShowCheckIcon,
    getError,
    validation,
    changeHandler,
    onHeaderClick,
    getConfidenceError,
    changeHandlerGender,
    changeHandlerIssuer,
    changeHandlerBirthDate,
    changeHandlerIssuerCode,
    changeHandlerSeriaNumber,
    changeHandlerBirthdayPlace,
  };
};
